import { Spacer, VDBText } from "@components/atoms";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import React, { useState, useCallback, useMemo, memo, useRef } from "react";
import {
  Platform,
  TouchableOpacity,
  Linking,
  Pressable,
  View,
  Modal,
  Dimensions,
  StyleSheet,
  LayoutChangeEvent,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Tooltip from "@components/atoms/Tooltip";
import TooltipButton from "@scenes/pdp/utils/TooltipButton";
import styled from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useUserInfo } from "@redux/slices/userInfoSlice";

export type StatsBreakdown = {
  marketplacePlan?: number;
  purchasedAdditionalUsers: number;
  supplierSolutionAccess: number;
  totalUsers: number;
  licensedUsers?: number;
};

type StatsTooltipContentProps = {
  statsData: StatsBreakdown;
  typography: any;
  theme: any;
  translate: (key: string, defaultValue: string) => string;
  hasNoPlan: boolean;
  onViewPricing: () => void;
};

const StatsTooltipContent = memo(
  ({
    statsData,
    typography,
    theme,
    translate,
    hasNoPlan,
    onViewPricing,
  }: StatsTooltipContentProps) => {
    return (
      <StatsTooltipContainer>
        <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
          {translate(
            "account.tooltip.stats.header",
            "Your user breakdown is shown below."
          )}
        </VDBText>
        {hasNoPlan && (
          <>
            <Spacer size={SpaceType.Basic} />
            <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
              {translate(
                "account.tooltip.stats.description",
                "Need more users? Add a marketplace plan, listing, or another VDB solution."
              )}
            </VDBText>
            <Spacer size={SpaceType.Basic} />
            <TouchableOpacity onPress={onViewPricing}>
              <VDBText type={typography["Body-M"]} color={theme["link-static"]}>
                {translate("account.tooltip.stats.viewPricing", "View Pricing")}
              </VDBText>
            </TouchableOpacity>
          </>
        )}
        <Spacer size={SpaceType.Basic} />
        <Divider />
        <Spacer size={SpaceType.Basic} />
        {statsData?.marketplacePlan !== undefined &&
          statsData?.marketplacePlan >= 0 && (
            <>
              <StatsRow>
                <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
                  {translate(
                    "account.tooltip.stats.marketplacePlan",
                    "Marketplace Plan"
                  )}
                </VDBText>
                <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
                  {statsData?.marketplacePlan}
                </VDBText>
              </StatsRow>
              <Spacer size={SpaceType.Small} />
            </>
          )}
        <StatsRow>
          <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
            {translate(
              "account.tooltip.stats.purchasedAdditionalUsers",
              "Purchased Additional Users"
            )}
          </VDBText>
          <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
            {statsData.purchasedAdditionalUsers}
          </VDBText>
        </StatsRow>
        <Spacer size={SpaceType.Small} />
        <StatsRow>
          <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
            {translate(
              "account.tooltip.stats.supplierSolutionAccess",
              "Included Supplier & Solution Users"
            )}
          </VDBText>
          <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
            {statsData.supplierSolutionAccess}
          </VDBText>
        </StatsRow>
        {(statsData?.licensedUsers ?? 0) > 0 && (
          <>
            <Spacer size={SpaceType.Small} />
            <StatsRow>
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <VDBIcon
                  name={VDBIcons.WARNING}
                  size={16}
                  color={theme["error-01"]}
                  variant={IconsVariant.VDBCustom}
                />
                <Spacer size={SpaceType.ExtraSmall} />
                <VDBText type={typography["Minor-R"]} color={theme["error-01"]}>
                  {translate(
                    "account.tooltip.stats.licensedUsers",
                    "Users to Review"
                  )}
                </VDBText>
              </View>
              <VDBText type={typography["Minor-R"]} color={theme["error-01"]}>
                {statsData.licensedUsers}
              </VDBText>
            </StatsRow>
          </>
        )}
        <Spacer size={SpaceType.Basic} />
        <Divider />
        <Spacer size={SpaceType.Basic} />
        <StatsRow>
          <VDBText type={typography["Minor-M"]} color={theme["core-05"]}>
            {translate("account.tooltip.stats.totalUsers", "Total Users")}
          </VDBText>
          <VDBText type={typography["Minor-M"]} color={theme["core-05"]}>
            {statsData.totalUsers}
          </VDBText>
        </StatsRow>
      </StatsTooltipContainer>
    );
  }
);

type InfoTooltipContentProps = {
  text: string;
  stats: boolean;
  statsData?: StatsBreakdown;
  typography: any;
  theme: any;
  translate: (key: string, defaultValue: string) => string;
  hasNoPlan: boolean;
  onViewPricing: () => void;
};

const InfoTooltipContent = memo(
  ({
    text,
    stats,
    statsData,
    typography,
    theme,
    translate,
    hasNoPlan,
    onViewPricing,
  }: InfoTooltipContentProps) => (
    <TooltipContentView>
      {stats && statsData ? (
        <StatsTooltipContent
          statsData={statsData}
          typography={typography}
          theme={theme}
          translate={translate}
          hasNoPlan={hasNoPlan}
          onViewPricing={onViewPricing}
        />
      ) : (
        <TextTooltipContainer>
          <VDBText
            color={theme["core-06"]}
            type={typography["Minor-R"]}
            textAlign="left"
          >
            {text}
          </VDBText>
        </TextTooltipContainer>
      )}
    </TooltipContentView>
  )
);

const defaultTranslate = (key: string, defaultValue: string) => defaultValue;

const TOOLTIP_SCREEN_MARGIN = 12;
const TOOLTIP_GAP = 2;
const TOOLTIP_ARROW_SIZE = 10;

type AnchorRect = { x: number; y: number; width: number; height: number };

const NativeTooltip = ({
  width,
  backgroundColor,
  trigger,
  children,
}: {
  width: number;
  backgroundColor: string;
  trigger: React.ReactNode;
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [anchor, setAnchor] = useState<AnchorRect>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [contentHeight, setContentHeight] = useState(0);
  const triggerRef = useRef<View>(null);
  const insets = useSafeAreaInsets();
  const screen = Dimensions.get("window");

  const handleOpen = useCallback(() => {
    triggerRef.current?.measureInWindow((x, y, w, h) => {
      setAnchor({ x, y, width: w, height: h });
      setContentHeight(0);
      setIsOpen(true);
    });
  }, []);

  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleContentLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const height = e.nativeEvent.layout.height;
      if (height && Math.abs(height - contentHeight) > 1) {
        setContentHeight(height);
      }
    },
    [contentHeight]
  );

  const anchorCenterX = anchor.x + anchor.width / 2;
  const left = Math.max(
    TOOLTIP_SCREEN_MARGIN,
    Math.min(
      anchorCenterX - width / 2,
      screen.width - width - TOOLTIP_SCREEN_MARGIN
    )
  );

  const spaceAbove = anchor.y - insets.top;
  const spaceBelow = screen.height - (anchor.y + anchor.height) - insets.bottom;
  const neededSpace = contentHeight + TOOLTIP_GAP + TOOLTIP_ARROW_SIZE;
  const placeAbove = spaceAbove >= neededSpace || spaceAbove >= spaceBelow;

  let top = placeAbove
    ? anchor.y - contentHeight - TOOLTIP_GAP - TOOLTIP_ARROW_SIZE
    : anchor.y + anchor.height + TOOLTIP_GAP + TOOLTIP_ARROW_SIZE;
  top = Math.max(
    insets.top + TOOLTIP_SCREEN_MARGIN,
    Math.min(top, screen.height - contentHeight - TOOLTIP_SCREEN_MARGIN)
  );

  const arrowLeft = Math.max(
    TOOLTIP_ARROW_SIZE,
    Math.min(
      anchorCenterX - left - TOOLTIP_ARROW_SIZE / 2,
      width - TOOLTIP_ARROW_SIZE * 2
    )
  );

  return (
    <>
      <Pressable ref={triggerRef} onPress={handleOpen}>
        {trigger}
      </Pressable>
      <Modal
        visible={isOpen}
        transparent
        animationType="fade"
        onRequestClose={handleClose}
        supportedOrientations={["portrait", "landscape"]}
      >
        <Pressable style={StyleSheet.absoluteFill} onPress={handleClose} />
        <View
          onLayout={handleContentLayout}
          style={{
            position: "absolute",
            left,
            top,
            width,
            backgroundColor,
            borderRadius: 8,
            opacity: contentHeight ? 1 : 0,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
            elevation: 8,
          }}
        >
          <Pressable onPress={handleClose}>{children}</Pressable>
          <View
            pointerEvents="none"
            style={{
              position: "absolute",
              left: arrowLeft,
              [placeAbove ? "bottom" : "top"]: -TOOLTIP_ARROW_SIZE / 2,
              width: TOOLTIP_ARROW_SIZE,
              height: TOOLTIP_ARROW_SIZE,
              backgroundColor,
              transform: [{ rotate: "45deg" }],
            }}
          />
        </View>
      </Modal>
    </>
  );
};

const TooltipComponent = ({
  tooltipText,
  typography,
  theme,
  stats,
  statsData,
  t,
}: {
  tooltipText: string;
  typography: any;
  theme: any;
  stats: boolean;
  statsData?: StatsBreakdown;
  t?: (key: string, defaultValue: string) => string;
}) => {
  const userInfo = useUserInfo();
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);

  const tooltipWidth = stats ? 280 : 200;
  const webTooltipWidth = stats ? "320px" : "200px";

  const translate = useMemo(() => t || defaultTranslate, [t]);
  const hasNoPlan = userInfo.hasNoPlan();

  const handleViewPricing = useCallback(() => {
    Linking.openURL("https://vdbapp.com/?menu=solutions");
  }, []);

  const tooltipContentProps = useMemo(
    () => ({
      text: tooltipText,
      stats,
      statsData,
      typography,
      theme,
      translate,
      hasNoPlan,
      onViewPricing: handleViewPricing,
    }),
    [
      tooltipText,
      stats,
      statsData,
      typography,
      theme,
      translate,
      hasNoPlan,
      handleViewPricing,
    ]
  );

  const iconElement = (
    <VDBIcon
      name={VDBIcons.QUESTION}
      size={20}
      color={theme["core-06"]}
      variant={IconsVariant.VDBCustom}
    />
  );

  // Web / Large Screen: Use TooltipButton (hover)
  if (Platform.OS === "web" && theme.isLargeScreen) {
    return (
      <TooltipButton
        enableTooltip={true}
        renderComponent={<InfoTooltipContent {...tooltipContentProps} />}
        width={webTooltipWidth}
        position="top"
        backgroundColor={theme["core-01"]}
        textColor={theme["core-06"]}
      >
        {iconElement}
      </TooltipButton>
    );
  }

  // Mobile / Tablet: press to open, tap outside (backdrop) to dismiss.
  // Custom Modal + Pressable backdrop instead of react-native-walkthrough-tooltip
  // whose outside-tap dismissal broke under RN 0.85 (Expo SDK 56) Fabric.
  if (Platform.OS === "android" || Platform.OS === "ios") {
    return (
      <NativeTooltip
        width={tooltipWidth}
        backgroundColor={theme["core-01"]}
        trigger={iconElement}
      >
        <InfoTooltipContent {...tooltipContentProps} />
      </NativeTooltip>
    );
  }

  return (
    <Tooltip
      isOpen={tooltipIsOpen}
      setIsOpen={setTooltipIsOpen}
      trigger={() => (
        <VDBIcon
          name={VDBIcons.QUESTION}
          size={20}
          color={theme["core-06"]}
          variant={IconsVariant.VDBCustom}
        />
      )}
      showOnPress={true}
      tooltipBackgroundColor={theme["core-01"]}
      popoverBackdropColor="transparent"
      placement="top"
      alignment="flex-start"
      popoverContentProps={{ width: tooltipWidth }}
    >
      <InfoTooltipContent {...tooltipContentProps} />
    </Tooltip>
  );
};

const TooltipContentView = styled.View``;

const TextTooltipContainer = styled.View`
  padding: ${SpaceType.Basic}px;
`;

const StatsTooltipContainer = styled.View`
  padding: ${SpaceType.Basic}px;
`;

const StatsRow = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const Divider = styled.View`
  height: 1px;
  background-color: ${({ theme }: { theme: any }) => theme["core-03"]};
`;

export default TooltipComponent;
